"use client";
import { cn } from "@/lib/utils";
import { IconMenu2, IconX } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
} from "motion/react";
import logo from "@/images/logo2.png";
import React, { useRef, useState } from "react";

export const Navbar = ({ children, className }) => {
  const ref = useRef(null);
  const { scrollY } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const [visible, setVisible] = useState(false);

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 100) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  });

  return (
    <motion.div
      ref={ref}
      // IMPORTANT: Change this to class of `fixed` if you want the navbar to be fixed
      className={cn("sticky inset-x-0 top-4 sm:top-6 lg:top-20 z-40 w-full", className)}>
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child, { visible })
          : child,
      )}
    </motion.div>
  );
};

export const NavBody = ({ children, className, visible }) => {
  return (
    <motion.div
      animate={{
        backdropFilter: visible ? "blur(16px)" : "blur(8px)",
        boxShadow: visible
          ? "0 8px 32px rgba(152, 196, 84, 0.1), 0 2px 8px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(152, 196, 84, 0.1)"
          : "0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)",
        width: visible ? "75%" : "100%",
        y: visible ? 20 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 25,
      }}
      className={cn(
        "relative z-[60] mx-auto hidden w-full max-w-7xl flex-row items-center justify-between self-start rounded-full bg-white/95 px-4 xl:px-8 py-3 lg:flex dark:bg-neutral-950/95 shadow-xl border-t-2 border-t-[#98C454]/30 border-x border-x-white/20 border-b border-b-white/20",
        visible && "bg-white/90 dark:bg-neutral-950/90",
        className,
      )}>
      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#98C454]/5 via-transparent to-[#87b043]/5 pointer-events-none"></div>
      <div className="relative z-10 w-full flex items-center justify-between">
        {children}
      </div>
    </motion.div>
  );
};

export const NavItems = ({ items, className, onItemClick }) => {
  const [hovered, setHovered] = useState(null);

  return (
    <motion.div
      onMouseLeave={() => setHovered(null)}
      className={cn(
        "absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-sm font-medium text-zinc-700 transition duration-200 lg:flex",
        className,
      )}>
      {items.map((item, idx) => {
        const isHashLink = item.link?.startsWith("#");
        const isExternal = item.external;
        const Component = isHashLink || isExternal ? "a" : Link;
        const linkProps = isHashLink || isExternal
          ? { href: item.link, target: isExternal ? "_blank" : undefined, rel: isExternal ? "noopener noreferrer" : undefined }
          : { to: item.link ?? "#" };
        return (
          <Component
            key={`link-${idx}`}
            onMouseEnter={() => setHovered(idx)}
            onClick={onItemClick}
            className="relative px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-neutral-300 whitespace-nowrap transition-colors duration-200 hover:text-[#98C454] group"
            {...linkProps}>
            {hovered === idx && (
              <motion.div
                layoutId="hovered"
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 h-full w-full rounded-full bg-gradient-to-r from-[#98C454]/10 to-[#87b043]/10 shadow-sm"
              />
            )}
            <span className="relative z-20">{item.name}</span>
            {/* Animated underline */}
            <motion.div
              className="absolute bottom-1 left-1/2 h-0.5 bg-gradient-to-r from-[#98C454] to-[#87b043] rounded-full"
              initial={{ width: 0, x: "-50%" }}
              animate={hovered === idx ? { width: "70%", x: "-50%" } : { width: 0, x: "-50%" }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />
          </Component>
        );
      })}
    </motion.div>
  );
};

export const MobileNav = ({ children, className, visible }) => {
  return (
    <motion.div
      animate={{
        backdropFilter: visible ? "blur(16px)" : "blur(8px)",
        boxShadow: visible
          ? "0 8px 32px rgba(152, 196, 84, 0.12), 0 2px 8px rgba(0, 0, 0, 0.1)"
          : "0 4px 20px rgba(0, 0, 0, 0.08)",
        width: visible ? "92%" : "100%",
        paddingRight: visible ? "16px" : "0px",
        paddingLeft: visible ? "16px" : "0px",
        borderRadius: visible ? "16px" : "24px",
        y: visible ? 20 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 25,
      }}
      className={cn(
        "relative z-50 mx-auto flex w-full max-w-[calc(100vw-2rem)] flex-col items-center justify-between bg-white/95 px-0 py-3 lg:hidden shadow-lg border-t-2 border-t-[#98C454]/30 border-x border-x-white/20 border-b border-b-white/20",
        visible && "bg-white/90 dark:bg-neutral-950/90",
        className,
      )}>
      <div className="absolute inset-0 rounded-[24px] bg-gradient-to-br from-[#98C454]/5 via-transparent to-[#87b043]/5 pointer-events-none"></div>
      <div className="relative z-10 w-full">
        {children}
      </div>
    </motion.div>
  );
};

export const MobileNavHeader = ({ children, className }) => {
  return (
    <div
      className={cn(
        "flex w-full flex-row items-center justify-between",
        className,
      )}>
      {children}
    </div>
  );
};

export const MobileNavMenu = ({ children, className, isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            opacity: { duration: 0.2 }
          }}
          className={cn(
            "absolute inset-x-0 top-16 z-50 flex w-full flex-col items-start justify-start gap-2 rounded-2xl bg-white/98 backdrop-blur-xl px-2 py-6 shadow-[0_8px_32px_rgba(152,196,84,0.15),_0_2px_12px_rgba(0,0,0,0.1)] border border-[#98C454]/10 dark:bg-neutral-950/98 max-h-[80vh] overflow-y-auto safe-bottom",
            className,
          )}>
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const MobileNavToggle = ({ isOpen, onClick }) => {
  return (
    <motion.button
      whileTap={{ scale: 0.9 }}
      whileHover={{ scale: 1.05 }}
      onClick={onClick}
      className="p-2 rounded-full hover:bg-[#98C454]/10 transition-colors duration-200"
    >
      {isOpen ? (
        <IconX className="text-gray-800 dark:text-white w-6 h-6" />
      ) : (
        <IconMenu2 className="text-gray-800 dark:text-white w-6 h-6" />
      )}
    </motion.button>
  );
};

export const NavbarLogo = () => {
  return (
    <Link
      to="/"
      className="relative z-20 mr-2 lg:mr-6 flex items-center space-x-2 px-2 py-1 text-sm font-normal text-black transition-transform duration-200 hover:scale-105">
      <motion.img
        src={logo}
        alt="logo"
        className="h-9 sm:h-10 lg:h-12 w-auto"
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
      />
    </Link>
  );
};

export const NavbarButton = ({
  href,
  as,
  children,
  className,
  variant = "primary",
  ...props
}) => {
  const baseStyles =
    "px-5 py-2.5 rounded-full text-sm font-bold relative cursor-pointer inline-block text-center transition-all duration-300 ease-out overflow-hidden";

  const variantStyles = {
    primary:
      "bg-gradient-to-r from-[#98C454] to-[#87b043] text-white shadow-[0_4px_16px_rgba(152,196,84,0.3)] hover:shadow-[0_8px_32px_rgba(152,196,84,0.5),_0_0_20px_rgba(152,196,84,0.3)] hover:scale-105 hover:-translate-y-0.5",
    secondary: "bg-white/50 backdrop-blur-sm text-gray-700 shadow-sm hover:bg-white/80 hover:shadow-[0_4px_12px_rgba(152,196,84,0.15)] hover:scale-105 border border-gray-200/50 hover:border-[#98C454]/20 dark:bg-neutral-800/50 dark:text-white dark:border-neutral-700/50",
    dark: "bg-gradient-to-r from-gray-800 to-gray-900 text-white shadow-[0_4px_16px_rgba(0,0,0,0.2)] hover:shadow-[0_6px_24px_rgba(0,0,0,0.3)] hover:scale-105 hover:-translate-y-0.5",
    gradient:
      "bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-[0_4px_16px_rgba(59,130,246,0.3)] hover:shadow-[0_6px_24px_rgba(59,130,246,0.4)] hover:scale-105 hover:-translate-y-0.5",
  };

  // Determine the component to use
  let Tag = as;
  let linkProps = {};

  if (!Tag) {
    if (href) {
      Tag = Link;
      linkProps = { to: href };
    } else {
      Tag = "button";
    }
  } else if (Tag === "a" && href) {
    Tag = Link;
    linkProps = { to: href };
  } else if (href) {
    linkProps = { href };
  }

  return (
    <motion.div
      className="relative"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Tag
        className={cn(baseStyles, variantStyles[variant], className)}
        {...linkProps}
        {...props}>
        {/* Shine effect overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          initial={{ x: "-100%" }}
          whileHover={{ x: "100%" }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        />
        <span className="relative z-10">{children}</span>
      </Tag>
    </motion.div>
  );
};
